/* The section / navigator model, ported from app.ts (sections() ~564,
   currentSection() ~828, teamAreas ~540, the picker trees ~2133/2221, and the
   heading derivations ~2555). It is a PURE function of the store: given the
   live state it returns the drawer's sections, each with its File menu, area
   tabs, and picker tree, and its ops call the store's actions. The chrome
   components (Drawer, Navigator, Picker) read this — they never re-derive the
   section shape themselves, so a section can never show another's navigator.

   Record-mutating ops (state transitions, delete) and on-nav reads live on the
   Store; the form/record VIEWS they open belong to the surface agents. */
import type { Team, Recorded } from "../../../shared/teams";
import type { Project, RecordState } from "../../../shared/projects";
import { subtreeIds, type Directory, type DirectorySection } from "../../../shared/directories";
import { enabled, type FeatureId } from "../../../shared/features";
import { esc } from "../../esc";
import { hint } from "./helpText";
import type { ClientState } from "./state";
import type { Store } from "./store";

/* ---- section-model types (the vanilla shapes, minus the `doc` parameter:
   React owns the document, so ops just call store actions) ---- */

export interface SectionView {
  label: string;
  current: () => boolean;
  select?: () => void;
  count?: () => { n: number; noun: string } | null;
  files?: () => { id: string; name: string }[];
  openFile?: (id: string) => void;
  openId?: () => string | null;
}

export interface SectionDir {
  section: DirectorySection;
  noun: string;
  records: () => { id: string; name: string; status: string; directoryId: string | null }[];
  open: (id: string) => void;
  openId: () => string | null;
}

export interface SectionOp {
  label: string;
  run: () => void;
}

export interface SectionMenu {
  label: string;
  ops: SectionOp[];
}

export interface SectionTab {
  label: string;
  note: string;
  featureId?: FeatureId;
}

export interface Section {
  label: string;
  featureId?: FeatureId;
  views: SectionView[];
  dir?: SectionDir;
  menus: SectionMenu[];
  tabs: SectionTab[];
  open: () => void;
}

/* ---- constants, verbatim from app.ts ---- */

export const PROJECT_AREAS: SectionTab[] = [
  { label: "Resourcing", note: "" },
  { label: "Charter", featureId: "project-charter", note: "" },
  {
    label: "Roles & RACI",
    featureId: "project-roles-raci",
    note: "Roles & RACI — not recorded in the app yet. This area is planned. The roles a project needs, and who fills them, are recorded per project on its card today.",
  },
  {
    label: "Deliverables",
    featureId: "project-deliverables",
    note: "Deliverables — not recorded in the app yet. This area is planned: what the project is expected to produce, and by when.",
  },
  {
    label: "Project Process",
    featureId: "project-process",
    note: "Project Process — not recorded in the app yet. This area is planned: the processes and gates a project owes, which should be derived from its stage and scope rather than hand-typed.",
  },
  {
    label: "Change Request",
    featureId: "project-change-request",
    note: "Change Request — not built yet. This area is planned: changing an active project should raise a request that keeps the old version and applies the edit once approved.",
  },
];

/* the PDP catalog's three areas (pdp-catalog.md PC1) — one section, three
   tabs over the same fetched catalog; the view keys on the label */
export const PDP_TABS: SectionTab[] = [
  { label: "Deliverables", note: "" },
  { label: "Phases & exit criteria", note: "" },
  { label: "Gates", note: "" },
  /* the baseline templates, an Admin catalog beside the PDP's own
     (product-baselines.md BL25) */
  { label: "Baseline templates", note: "", featureId: "baselines" },
];

/* a product record's two areas (products-pdp.md PP15): the Phase area is the
   record's front page, Deliverables the owed set */
export const PRODUCT_AREAS: SectionTab[] = [
  { label: "Phase", note: "" },
  { label: "Deliverables", note: "" },
  /* what the product's team agreed to (product-baselines.md BL25) */
  { label: "Baselines", note: "", featureId: "baselines" },
];

export const TEAM_AREAS: SectionTab[] = [
  { label: "Roster", note: "" },
  {
    label: "Org Chart",
    featureId: "team-org-chart",
    note: "Org Chart — not recorded in the app yet. This area is planned: the reporting line above and below this team. Today the directory is the only place that records it, one manager at a time.",
  },
  {
    label: "Charge Codes",
    featureId: "team-charge-codes",
    note: "Charge Codes — not recorded for teams. This area is planned; today a charge code is recorded per project, effective-dated, not per team.",
  },
];

/* ---- derivations, ported verbatim (read state, never mutate) ---- */

export function teamTitle(t: Team): string {
  if (t.manager.state === "missing" || t.nameDerivedFromManager) return t.name;
  return `${t.name} - ${t.manager.value}`;
}

const openProject_ = (s: ClientState): Project | undefined =>
  s.projects.find((p) => p.id === s.openProjectId);

const openTeam_ = (s: ClientState): Team | undefined =>
  s.teams.find((t) => t.id === s.openTeamId);

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const stampText = (r: { source: string; date: string; by?: { name: string } }): string =>
  `${esc(r.source)}${r.by ? ` by ${esc(r.by.name)}` : ""} · ${fmtDate(r.date)}`;

export const roleName = (s: ClientState, roleId: string): string =>
  s.roles.find((r) => r.id === roleId)?.name ?? roleId;

/* the reader-facing name of the record whose own areas are on screen (N20/N25) */
export function openRecordName(s: ClientState): string | undefined {
  if (!s.area) return undefined;
  if (s.view === "teams") {
    const t = openTeam_(s);
    return t && teamTitle(t);
  }
  if (s.view === "products") return s.products.find((p) => p.id === s.openProductId)?.name;
  return s.view === "projects" ? openProject_(s)?.name : undefined;
}

/* the open record's state + last-recorded stamp, beside its heading (teams-page
   D14 applied to projects and items). HTML string — rendered via innerHTML. */
export function recordStampLine(s: ClientState): string {
  if (!s.area) return "";
  if (s.view === "projects") {
    const p = openProject_(s);
    return p
      ? `<div class="nav-stamp"><span class="state record-state-open ${esc(p.recordState)}"${hint(s.me.surface, "state-badge")}>${esc(p.recordState)}</span>
         <span class="stamp"${hint(s.me.surface, "stamp")}>${stampText(p.recorded)}</span></div>`
      : "";
  }
  return "";
}

export function managerStampLine(s: ClientState): string {
  if (s.view !== "teams" || !s.area) return "";
  const t = openTeam_(s);
  if (!t) return "";
  const m = t.manager;
  if (m.state === "missing") return "";
  const badge = m.state === "unconfirmed" ? `<span class="state unconfirmed">unconfirmed</span>` : "";
  const why = m.state === "unconfirmed" ? `<span class="nav-why">${esc(m.why)}</span>` : "";
  return `<div class="nav-stamp">Manager${badge}<span class="stamp">${esc(m.source)}</span>${why}</div>`;
}

/* the roster's Manager gap row (teams-page D14) — exported for TeamsView */
export function managerGapRow(m: Recorded<string>): string {
  if (m.state !== "missing") return "";
  return `<li class="manager-slot"><span class="slot-main">
      <span class="slot-name">Manager<span class="state missing">not recorded</span></span>
      <div class="slot-note">${esc(m.why)}</div></span></li>`;
}

/* ---- the File menu's per-record ops (recordOps/itemOps/deleteOp) ---- */

function deleteOp<T>(record: T | undefined, run: (r: T) => void): SectionOp[] {
  return record ? [{ label: "Delete…", run: (): void => run(record) }] : [];
}

function recordOps(store: Store, p: Project | undefined): SectionOp[] {
  if (!p) return [];
  const op = (label: string, to: RecordState, verb: string): SectionOp => ({
    label,
    run: (): void => void store.setRecordState(p, to, verb),
  });
  const forward: Partial<Record<RecordState, SectionOp>> = {
    draft: op("Submit for review", "review", "submit"),
    review: op("Approve", "active", "approve"),
  };
  return p.recordState === "archived"
    ? [op("Unarchive", "active", "unarchive")]
    : [
        ...(forward[p.recordState] ? [forward[p.recordState] as SectionOp] : []),
        ...(p.recordState === "draft" ? [] : [op("Back to draft", "draft", "move")]),
        op("Archive", "archived", "archive"),
      ];
}

/* ---- sections(): the drawer/navigator model, ported verbatim ---- */

export function sections(store: Store): Section[] {
  const s = store.state;
  const all: Section[] = [
    /* the shared landing (designs/shared-home.md SH1): first, on every
       surface, no records — its doors are the page */
    {
      label: "Home",
      views: [{ label: "Home", current: (): boolean => s.view === "home" }],
      menus: [],
      tabs: [],
      open: (): void => store.selectNav("home"),
    },
    {
      label: "Intake",
      featureId: "intake" as const,
      views: [{ label: "Intake", current: (): boolean => s.view === "intake" }],
      menus: [],
      tabs: [],
      open: (): void => store.selectNav("intake"),
    },
    {
      label: "Projects",
      views: [{ label: "Projects", current: (): boolean => s.view === "projects" }],
      dir: {
        section: "projects",
        noun: "project",
        records: (): { id: string; name: string; status: string; directoryId: string | null }[] =>
          s.projects.map((p) => ({
            id: p.id,
            name: p.name,
            status: p.recordState,
            directoryId: p.directoryId,
          })),
        open: (id: string): void => store.openProject(id),
        openId: (): string | null => s.openProjectId,
      },
      menus: [
        {
          label: "File",
          ops: [
            { label: "New draft…", run: (): void => store.openForm(null) },
            { label: "View…", run: (): void => store.openPicker() },
            ...(s.openProjectId
              ? [
                  { label: "Edit…", run: (): void => store.openForm(s.openProjectId) },
                  ...recordOps(store, s.projects.find((p) => p.id === s.openProjectId)),
                  { label: "Close", run: (): void => store.closeProject() },
                  ...deleteOp(
                    s.projects.find((p) => p.id === s.openProjectId),
                    (p) => void store.deleteProject(p),
                  ),
                ]
              : []),
          ],
        },
      ],
      tabs: s.openProjectId ? PROJECT_AREAS.filter((t) => store.on(t.featureId)) : [],
      open: (): void => store.selectNav("projects"),
    },
    /* the Products section over the PDP (products-pdp.md PP14/PP15), the
       Teams shape: one view, its products as files, View… / Close only — no
       New draft, because products are added on the intake form */
    {
      label: "Products",
      featureId: "products" as const,
      views: [
        {
          label: "Products",
          current: (): boolean => s.view === "products",
          select: (): void => store.selectNav("products"),
          count: (): { n: number; noun: string } | null =>
            s.productsError ? null : { n: s.products.length, noun: "product" },
          files: (): { id: string; name: string }[] => s.products.map((p) => ({ id: p.id, name: p.name })),
          openFile: (id: string): void => store.openProduct(id),
          openId: (): string | null => s.openProductId,
        },
      ],
      menus: [
        {
          label: "File",
          ops: [
            { label: "View…", run: (): void => store.openPicker() },
            ...(s.openProductId ? [{ label: "Close", run: (): void => store.closeProduct() }] : []),
          ],
        },
      ],
      tabs: s.openProductId ? PRODUCT_AREAS.filter((t) => store.on(t.featureId)) : [],
      open: (): void => store.selectNav("products"),
    },
    /* the team list intake speaks, curated by Admins (teams-and-products.md
       TP8): name, type, the review queue, retire and merge */
    ...(s.me.roles.includes("admin")
      ? [
          {
            label: "Teams",
            views: [
              {
                label: "Teams",
                current: (): boolean => s.view === "team-admin",
                select: (): void => store.selectNav("team-admin"),
              },
            ],
            menus: [],
            tabs: [],
            open: (): void => store.selectNav("team-admin"),
          },
        ]
      : []),
    {
      /* the Entra org chart — people and who they report to. Shown as Org so
         it is never confused with the team list intake speaks, which is the
         Admin-only Teams section (teams-and-products.md TP8). The view id
         stays "teams" so links and the record's areas keep working. */
      label: "Org",
      views: [
        {
          label: "Org",
          current: (): boolean => s.view === "teams",
          select: (): void => store.selectNav("teams"),
          count: (): { n: number; noun: string } | null =>
            s.teamsError ? null : { n: s.teams.length, noun: "team" },
          files: (): { id: string; name: string }[] =>
            s.teams.map((t) => ({ id: t.id, name: teamTitle(t) })),
          openFile: (id: string): void => store.openTeam(id),
          openId: (): string | null => s.openTeamId,
        },
      ],
      menus: [
        {
          label: "File",
          ops: [
            { label: "View…", run: (): void => store.openPicker() },
            ...(s.openTeamId ? [{ label: "Close", run: (): void => store.closeTeam() }] : []),
          ],
        },
      ],
      tabs: s.openTeamId ? teamAreas(s) : [],
      open: (): void => store.selectNav("teams"),
    },
    {
      label: "Roles",
      featureId: "roles-catalog" as const,
      views: [
        {
          label: "Roles",
          current: (): boolean => s.view === "roles",
          select: (): void => store.selectNav("roles"),
          count: (): { n: number; noun: string } | null => ({ n: s.roles.length, noun: "role" }),
        },
      ],
      menus: [{ label: "File", ops: [{ label: "View…", run: (): void => store.openPicker() }] }],
      tabs: [],
      open: (): void => store.selectNav("roles"),
    },
    /* the PDP template catalog, directly after Roles (pdp-catalog.md PC1):
       no records to pick, so no File menu — the three tabs are the whole
       navigator. The count is standing rows only. */
    {
      label: "PDP",
      featureId: "pdp-catalog" as const,
      views: [
        {
          label: "PDP",
          current: (): boolean => s.view === "pdp",
          select: (): void => store.selectNav("pdp"),
          /* the catalog is read on navigation, so before that the list is
             empty — an empty list is "not loaded", never a count of 0 */
          count: (): { n: number; noun: string } | null =>
            s.pdp.deliverables.length
              ? { n: s.pdp.deliverables.filter((d) => !d.retired).length, noun: "deliverable" }
              : null,
        },
      ],
      menus: [],
      tabs: PDP_TABS.filter((t) => store.on(t.featureId)),
      open: (): void => store.selectNav("pdp"),
    },
    ...(s.me.roles.includes("admin")
      ? [
          {
            label: "Permissions",
            views: [
              {
                label: "Permissions",
                current: (): boolean => s.view === "permissions",
                select: (): void => store.selectNav("permissions"),
                count: (): { n: number; noun: string } | null => ({
                  n: s.users.length,
                  noun: "person",
                }),
              },
            ],
            menus: [{ label: "File", ops: [{ label: "View…", run: (): void => store.openPicker() }] }],
            tabs: [],
            open: (): void => store.selectNav("permissions"),
          },
        ]
      : []),
    /* the issue tracker (issue-tracker.md IT9): everyone sees it — My issues
       for a requester, All issues for an Admin; the server decides which.
       Before About Hermes, which stays last (help-layer.md HL4). */
    {
      label: "Issues",
      featureId: "issues" as const,
      views: [
        {
          label: "Issues",
          current: (): boolean => s.view === "issues",
          select: (): void => store.selectNav("issues"),
        },
      ],
      menus: [],
      tabs: [],
      open: (): void => store.selectNav("issues"),
    },
    {
      label: "About Hermes",
      featureId: "about-hermes" as const,
      views: [{ label: "About Hermes", current: (): boolean => s.view === "about-hermes" }],
      menus: [],
      tabs: [],
      open: (): void => store.selectNav("about-hermes"),
    },
  ];
  return all.filter((sec) => store.on(sec.featureId));
}

export function currentSection(store: Store): Section | null {
  return sections(store).find((sec) => sec.views.some((v) => v.current())) ?? null;
}

/* The areas of THIS team, for THIS reader — the surface filter alone. The
   per-team Allocations gate went with allocations (MVP cut, Sep 14 2026):
   what is left on a team record is the same for every reader who can see
   the team at all. */
export function teamAreas(s: ClientState): SectionTab[] {
  return TEAM_AREAS.filter((a) => a.featureId === undefined || enabled(a.featureId, s.me.surface));
}

/* ---- picker trees, ported verbatim ---- */

export interface PickerNode {
  view: SectionView;
  files: { id: string; name: string }[] | null;
  open: boolean;
}

const matches = (text: string, q: string): boolean => text.toLowerCase().includes(q);

export function pickerTree(s: ClientState, section: Section): PickerNode[] {
  const q = s.pickerSearch.trim().toLowerCase();
  return section.views
    .map((view) => {
      const folderHit = !q || matches(view.label, q);
      const all = view.files?.() ?? null;
      return {
        view,
        files: all && (folderHit ? all : all.filter((r) => matches(r.name, q))),
        open: q ? true : s.expandedFolders.includes(view.label),
      };
    })
    .filter(({ view, files }) => !q || (files ? files.length > 0 : matches(view.label, q)));
}

export type DirRowItem =
  | { kind: "dir"; dir: Directory; depth: number; open: boolean; count: number }
  | { kind: "unfiled"; depth: number; open: boolean; count: number }
  | { kind: "record"; rec: { id: string; name: string; status: string }; depth: number };

function dirOf(directoryId: string | null, known: Set<string>): string | null {
  return directoryId && known.has(directoryId) ? directoryId : null;
}

export function dirPickerRows(s: ClientState, sd: SectionDir): DirRowItem[] {
  const q = s.pickerSearch.trim().toLowerCase();
  const dirs = s.directories.filter((d) => d.section === sd.section);
  const known = new Set(dirs.map((d) => d.id));
  const recs = sd.records();
  const recsIn = (id: string | null) => recs.filter((r) => dirOf(r.directoryId, known) === id);
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  /* the count is the SUBTREE's, so it matches what selecting the folder
     shows (DIR12) — a collapsed parent must never read 0 over a filled
     child. 0 is still said out loud when the subtree is genuinely empty. */
  const countUnder = (id: string): number => {
    const scope = subtreeIds(id, dirs);
    return recs.filter((r) => {
      const filed = dirOf(r.directoryId, known);
      return filed !== null && scope.has(filed);
    }).length;
  };

  const walk = (parentId: string | null, depth: number, keepAll: boolean): DirRowItem[] =>
    dirs
      .filter((d) => d.parentId === parentId)
      .sort(byName)
      .flatMap((d) => {
        const keep = keepAll || !q || matches(d.name, q);
        const children = walk(d.id, depth + 1, keep);
        const records = recsIn(d.id)
          .sort(byName)
          .filter((r) => keep || matches(r.name, q))
          .map((r): DirRowItem => ({ kind: "record", rec: r, depth: depth + 1 }));
        const open = q ? true : s.expandedDirs.includes(d.id);
        const shown = keep || children.length > 0 || records.length > 0;
        if (!shown) return [];
        const row: DirRowItem = { kind: "dir", dir: d, depth, open, count: countUnder(d.id) };
        return open ? [row, ...children, ...records] : [row];
      });

  const tree = walk(null, 0, false);
  const unfiledRecs = recsIn(null)
    .sort(byName)
    .filter((r) => !q || matches(r.name, q) || matches("not filed", q));
  const unfiledOpen = q ? true : s.expandedDirs.includes("");
  const unfiled: DirRowItem[] =
    q && unfiledRecs.length === 0
      ? []
      : [
          { kind: "unfiled", depth: 0, open: unfiledOpen, count: recsIn(null).length },
          ...(unfiledOpen
            ? unfiledRecs.map((r): DirRowItem => ({ kind: "record", rec: r, depth: 1 }))
            : []),
        ];
  return [...tree, ...unfiled];
}
